import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import { SettingsSaveModule, initialLanguage } from '@puzzlemania/shared';
import { getPlatformCapabilities } from '../../platforms/capabilities';
import { logLocaleDiagnostics } from './diagnostics';
import de from './locales/de.json';
import en from './locales/en.json';
import es from './locales/es.json';
import fr from './locales/fr.json';
import hi from './locales/hi.json';
import it from './locales/it.json';
import pl from './locales/pl.json';
import ptBR from './locales/pt-BR.json';
import ru from './locales/ru.json';
import tr from './locales/tr.json';

/**
 * The language the very first paint happens in.
 *
 * Two rules, in order:
 *
 * 1. `lng` was once hardcoded to 'en' regardless of any previously saved
 *    preference — SettingsScreen only calls `i18n.changeLanguage()` from its own
 *    click handler, never re-applying the persisted language at boot, so a
 *    language switch silently reverted to English on every reload
 *    (QA_FINDINGS.md #5). A stored choice therefore wins outright, here, before
 *    anything renders.
 * 2. With nothing stored, the player's own locale decides — `initialLanguage`
 *    reads `navigator.languages` and narrows it to what this platform ships.
 *    Until now only the CrazyGames embed did this (Module 41's SDK detection);
 *    the main site and the Android app opened in English for everyone, so a
 *    Hindi player had to go and find Settings before the product spoke their
 *    language.
 *
 * `locales` comes from the platform profile, not a literal, so the languages
 * this can resolve to are the ones the running build actually declares.
 * `initialLanguage` returns null only when a stored choice exists, which is
 * exactly when rule 1 supplies the value instead.
 *
 * ## Why `persist` is conditional (Module 46, WP-46.2)
 *
 * Detection happens once and is then authoritative, so the resolved value is
 * normally written here — except on a platform that supplies its own locale
 * asynchronously. There, this module still resolves and applies a browser guess
 * so the first paint is not in the wrong language, but writing it would settle
 * the question before the platform's own, higher-priority answer has arrived;
 * that composition root persists instead (`embed/blockjam/portalLocale.ts`).
 *
 * The capability is read rather than the platform being named, so this file
 * never learns it is sometimes running on CrazyGames.
 */
const { locales, providesLocale } = getPlatformCapabilities();

// WP-46.4 — logged BEFORE the resolve, not after, and that ordering is the
// whole point: detection now persists, so a diagnostic that ran afterwards would
// report `saved` on every boot and never once show which source actually
// decided. Dev builds only; the call and its marker are absent from production.
logLocaleDiagnostics();

const initialLng =
  initialLanguage(locales, undefined, { persist: !providesLocale }) ??
  SettingsSaveModule.load().language;

/**
 * Module 46 (WP-46.3) — the Tier-1 bundles, keyed by their registry code.
 *
 * Statically imported so a language switch is instant and offline-safe: every
 * bundle is already in the JS the player downloaded. `pt-BR` is the one
 * hyphenated key, and i18next handles it natively — the key here has to match
 * `LANGUAGE_REGISTRY`'s code exactly, because that is what the resolver returns
 * and what `SettingsPopup` stores.
 *
 * `locale-parity.test.ts` fails if a registry language has no bundle here, or a
 * bundle is missing a key `en.json` has. Both are silent failures otherwise: the
 * first shows the player English under their own language's name, the second
 * shows one English string in an otherwise translated screen.
 */
const resources = {
  en: { translation: en },
  hi: { translation: hi },
  es: { translation: es },
  'pt-BR': { translation: ptBR },
  de: { translation: de },
  fr: { translation: fr },
  pl: { translation: pl },
  tr: { translation: tr },
  it: { translation: it },
  ru: { translation: ru },
};

export const initPromise = i18n.use(initReactI18next).init({
  resources,
  lng: initialLng,
  fallbackLng: 'en',
  interpolation: { escapeValue: false },
});

export default i18n;

/**
 * Module 50 (WP-50.12) — AUD-017: `<html lang>` never changed with the language.
 *
 * The UI text was fully translated in every language the audit measured and the
 * document still claimed `lang="en"` throughout. That attribute is what a screen
 * reader picks a voice from, what a browser picks hyphenation and quotation rules
 * from, and what a translation tool reads to decide whether to offer anything —
 * so the cost falls entirely on the players least able to work around it.
 *
 * One listener, in the module that owns the i18n instance, so every entry point
 * (main site, embed, Android) gets it without knowing it exists. Set immediately
 * as well as on change: the initial language is resolved above and never fires
 * `languageChanged`.
 */
function syncDocumentLanguage(language: string): void {
  if (typeof document === 'undefined') return;
  document.documentElement.lang = language;
}

syncDocumentLanguage(i18n.language);
i18n.on('languageChanged', syncDocumentLanguage);
